/**
 * Resolve whether the current user is acting on behalf of another household
 * member, and if so the subject's full name.
 *
 * Caller supplies subjectUserId and optionally certId. When subjectUserId
 * differs from the logged-in user's id, the member's full name is looked up
 * in the certifications Redux slice.
 *
 * When certId is provided, the lookup is scoped to that specific cert.
 * When certId is absent (e.g. interview-based DVT URL carries no certificationId),
 * all certifications are searched until the subject is found.
 */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'src/redux/rootReducerType';
import type { Certification } from 'src/types/Certification';

export interface UseOnBehalfOfOptions {
  subjectUserId?: number | null;
  certId?: number | null;
}

export interface UseOnBehalfOfResult {
  isOnBehalf: boolean;
  subjectName: string | null;
}

export function useOnBehalfOf(
  options: UseOnBehalfOfOptions = {}
): UseOnBehalfOfResult {
  const currentUserId = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user?.id ?? null
  );
  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[] | undefined;

  const { subjectUserId, certId } = options;

  return useMemo<UseOnBehalfOfResult>(() => {
    if (
      subjectUserId != null &&
      currentUserId != null &&
      subjectUserId !== currentUserId
    ) {
      let member;
      if (certId != null) {
        // Scope lookup to the specific cert when certId is available.
        const cert = certifications?.find((c) => c.id === certId);
        member = cert?.cert_household?.cert_members?.find(
          (m) => m.user_id === subjectUserId
        );
      } else {
        // No certId (e.g. interview-based DVT URL) — search all certs.
        for (const cert of certifications ?? []) {
          member = cert.cert_household?.cert_members?.find(
            (m) => m.user_id === subjectUserId
          );
          if (member) {
            break;
          }
        }
      }
      const name = member
        ? `${member.first_name} ${member.last_name}`.trim() || null
        : null;
      return { isOnBehalf: true, subjectName: name };
    }

    return { isOnBehalf: false, subjectName: null };
  }, [subjectUserId, certId, currentUserId, certifications]);
}
